'use client';

import Link from 'next/link';
import { useState } from 'react';

import { appRoutes } from '@/appRoutes';
import {
  resetPasswordSchema,
  type ResetPasswordSchema,
} from '@/features/auth/schemas';
import { hardRedirect } from '@/features/auth/utils';
import { MainButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify, zodErrorsToFieldErrors } from '@web/utils';

import { useResetPasswordMutation } from '../api/authApi';
import { useBootstrapAuth } from '../hooks';

import styles from './AuthForm.module.scss';

type Props = {
  email: string;
  code: string;
  inviteCode?: string;
};

const resetPasswordFieldSchemas = resetPasswordSchema.shape;

export function ResetPasswordForm({ email, code, inviteCode = '' }: Props) {
  const bootstrapAuth = useBootstrapAuth();
  const [resetPassword, resetStatus] = useResetPasswordMutation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const trimmedInviteCode = inviteCode.trim();

  const [formData, setFormData] = useState<ResetPasswordSchema>({
    email,
    code,
    new_password: '',
    confirm_new_password: '',
    invite_code: trimmedInviteCode || undefined,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof ResetPasswordSchema, string>>
  >({});

  const isBusy = resetStatus.isLoading || isRedirecting;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const parsed = resetPasswordSchema.safeParse(formData);

    if (!parsed.success) {
      setErrors(zodErrorsToFieldErrors<ResetPasswordSchema>(parsed.error));
      return;
    }

    try {
      const { confirm_new_password: _ignore, ...payload } = parsed.data; // * API does not need confirm_new_password.
      const response = await resetPassword({
        ...payload,
        ...(trimmedInviteCode ? { invite_code: trimmedInviteCode } : {}),
      }).unwrap();
      const data = response?.data;

      notify({
        type: 'success',
        message:
          (response?.message as string) ??
          'Password reset successful. Please log in.',
      });

      if (data?.user || data?.workspace) {
        setIsRedirecting(true);
        bootstrapAuth({
          currentUser: data.user ?? null,
          currentWorkspaceSlug: data.workspace?.slug ?? null,
          isWorkspaceSetupRequired: data.is_workspace_setup_required ?? false,
          isWorkspaceOnboarded: data.workspace?.is_workspace_onboarded ?? null,
        });
        hardRedirect(appRoutes.home);
        return;
      }

      setTimeout(() => {
        notify({
          type: 'info',
          message: 'We will send you back to the login page in a few seconds.',
        });
      }, 2000);

      setTimeout(() => {
        hardRedirect(appRoutes.auth.login);
      }, 5000);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <form
      className={`${styles.form} ${styles.formActive}`}
      onSubmit={handleSubmit}
    >
      <SmartInput
        type="text"
        name="email"
        label="Email"
        value={formData.email}
        onChange={() => {}}
        schema={resetPasswordFieldSchemas.email}
        error={errors.email}
        readOnly
      />

      <SmartInput
        type="text"
        name="code"
        label="Code"
        value={formData.code}
        onChange={() => {}}
        schema={resetPasswordFieldSchemas.code}
        error={errors.code}
        readOnly
      />

      <SmartInput
        type="password"
        name="new_password"
        label="New Password"
        value={formData.new_password}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, new_password: value }))
        }
        schema={resetPasswordFieldSchemas.new_password}
        error={errors.new_password}
      />

      <SmartInput
        type="password"
        name="confirm_new_password"
        label="Confirm New Password"
        value={formData.confirm_new_password}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, confirm_new_password: value }))
        }
        schema={resetPasswordFieldSchemas.confirm_new_password}
        error={errors.confirm_new_password}
      />

      <div className={styles.formActions}>
        <MainButton
          type="submit"
          disabled={isBusy}
          isLoading={isBusy}
          ariaLabel="Reset password"
        >
          Reset password
        </MainButton>

        <Link href={appRoutes.auth.login} className="inlineLink">
          Back to log in
        </Link>
      </div>
    </form>
  );
}
